// Resumen en lenguaje claro de la clasificación a playoffs (wizard Formato).
// Solo describe; no cambia datos guardados ni reglas del backend.

export type PlayoffBracketPolicy = "auto_preliminary" | "auto_byes" | "manual";

export interface PlayoffQualificationInput {
  groupCount: number;
  playersPerGroup: number;
  positionsFrom: number;
  positionsTo: number;
  wildcardPosition: number;
  wildcardCount: number;
  bracketPolicy: PlayoffBracketPolicy;
  unit?: { singular: string; plural: string };
}

export interface PlayoffQualificationSummary {
  text: string | null;
  warnings: string[];
  directCount: number;
  wildcardCount: number;
  total: number;
  bracketSize: number;
}

function placeLabel(from: number, to: number): string {
  if (from === to) return `los ${from}.º lugares`;
  if (to === from + 1) return `los ${from}.º y ${to}.º lugares`;
  return `del ${from}.º al ${to}.º lugar`;
}

function nextPowerOfTwo(n: number): number {
  let size = 2;
  while (size < n) size *= 2;
  return size;
}

export function playoffQualificationSummary(input: PlayoffQualificationInput): PlayoffQualificationSummary {
  const unit = input.unit ?? { singular: "jugador", plural: "jugadores" };
  const groups = Math.max(1, Math.floor(input.groupCount) || 1);
  const perGroup = Math.max(0, Math.floor(input.playersPerGroup) || 0);
  const from = Math.floor(input.positionsFrom) || 0;
  const to = Math.floor(input.positionsTo) || 0;
  const wcCount = Math.max(0, Math.floor(input.wildcardCount) || 0);
  const wcPos = Math.floor(input.wildcardPosition) || 0;
  const warnings: string[] = [];
  const empty = { text: null, directCount: 0, wildcardCount: 0, total: 0, bracketSize: 0 };

  if (from < 1 || to < 1) {
    return { ...empty, warnings: ["Los lugares que clasifican deben ser 1 o mayor."] };
  }
  if (from > to) {
    return { ...empty, warnings: [`«Del lugar» (${from}) no puede ser mayor que «al lugar» (${to}).`] };
  }
  if (perGroup > 0 && to > perGroup) {
    warnings.push(`Cada grupo tiene ${perGroup} ${perGroup === 1 ? unit.singular : unit.plural}; no existe el ${to}.º lugar.`);
  }
  const effectiveTo = perGroup > 0 ? Math.min(to, perGroup) : to;
  const directPerGroup = Math.max(0, effectiveTo - from + 1);
  const directCount = directPerGroup * groups;

  let wildcards = 0;
  if (wcCount > 0) {
    if (wcPos < 1) {
      warnings.push("Indica de qué lugar salen los comodines.");
    } else if (wcPos >= from && wcPos <= to) {
      warnings.push(`Los ${wcPos}.º lugares ya clasifican directo; los comodines deben ser de otro lugar (por ejemplo, el ${to + 1}.º).`);
    } else if (perGroup > 0 && wcPos > perGroup) {
      warnings.push(`Cada grupo tiene ${perGroup} ${perGroup === 1 ? unit.singular : unit.plural}; no existe el ${wcPos}.º lugar para comodines.`);
    } else if (wcCount > groups) {
      warnings.push(`Pediste ${wcCount} comodines pero solo hay ${groups} ${groups === 1 ? "grupo" : "grupos"} (un ${wcPos}.º lugar por grupo). Pasarán como máximo ${groups}.`);
      wildcards = groups;
    } else {
      wildcards = wcCount;
    }
  }

  const total = directCount + wildcards;
  if (total < 2) {
    return { ...empty, directCount, wildcardCount: wildcards, total, warnings: [...warnings, "Con esta configuración clasifican menos de 2; no alcanza para un cuadro."] };
  }
  const bracketSize = nextPowerOfTwo(total);
  const missing = bracketSize - total;
  const u = (n: number) => (n === 1 ? unit.singular : unit.plural);

  let text = `Así queda: pasan ${placeLabel(from, effectiveTo)} de cada grupo (${directCount} ${u(directCount)})`;
  if (wildcards > 0) {
    text += ` + ${wildcards === 1 ? `el mejor ${wcPos}.º lugar` : `los ${wildcards} mejores ${wcPos}.º lugares`} (${wildcards === 1 ? "comodín" : "comodines"})`;
  }
  text += ` = ${total} ${u(total)} al cuadro de playoffs`;
  if (missing === 0) {
    text += ` (cuadro de ${bracketSize}, sin BYE).`;
  } else if (input.bracketPolicy === "auto_byes") {
    text += ` (cuadro de ${bracketSize} con ${missing} BYE: ${missing === 1 ? "el mejor clasificado descansa" : `los ${missing} mejores clasificados descansan`} la primera ronda).`;
  } else if (input.bracketPolicy === "auto_preliminary") {
    const prelimMatches = total - bracketSize / 2;
    text += ` (cuadro de ${bracketSize / 2} con ronda preliminar: los últimos ${prelimMatches * 2} clasificados juegan ${prelimMatches} ${prelimMatches === 1 ? "partido extra" : "partidos extra"} para entrar).`;
  } else {
    text += ` (no llenan un cuadro de ${bracketSize}: tú acomodas el cuadro en el Draw).`;
  }
  return { text, warnings, directCount, wildcardCount: wildcards, total, bracketSize };
}
